import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as Yup from "yup"

import useCurrencyFormatters from "@hooks/useCurrencyFormatters"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms/index"

import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import Dialog from "@molecules/Dialog/Dialog"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import { MutationErrorBase } from "@molecules/Form/GenericForm/GenericForm.types"
import NumberInput from "@molecules/Form/inputs/NumberInput/NumberInput"
import Select from "@molecules/Select/Select"

import { BILLS_ROUTES } from "@routes/bills"

import styles from "./BillApplyVendorCreditDialog.module.scss"
import { useApplicableVendorCreditsQuery } from "./BillApplyVendorCreditDialog.queries"
import {
    BillApplyVendorCreditDialogProps,
    BillApplyVendorCreditFormContentProps,
    BillApplyVendorCreditFormValues,
} from "./BillApplyVendorCreditDialog.types"

const formSchema: Yup.ObjectSchema<BillApplyVendorCreditFormValues> = Yup.object().shape({
    vendor_credit: Yup.string().required("Please select a vendor credit."),
    amount: Yup.string().required("Please enter an amount."),
})

function BillApplyVendorCreditFormContent(props: BillApplyVendorCreditFormContentProps) {
    const { form, isPending, hasApplied, nonFieldError, amountDue, vendorID, onCancel } = props

    const { formatCurrency } = useCurrencyFormatters()
    const {
        data: vendorCredits,
        isLoading: isLoadingVendorCredits,
        isError: isVendorCreditsError,
    } = useApplicableVendorCreditsQuery(vendorID)

    // The most a credit can give this bill: what it has left, never more than the bill still owes.
    const getCeiling = useCallback(
        (vendorCreditID: string | undefined) => {
            const vendorCredit = vendorCredits?.find((candidate) => candidate.id === vendorCreditID)
            if (vendorCredit) {
                return Number(vendorCredit.amount_remaining) < Number(amountDue)
                    ? vendorCredit.amount_remaining
                    : amountDue
            } else {
                return null
            }
        },
        [vendorCredits, amountDue],
    )

    const vendorCreditOptions = useMemo(
        () =>
            (vendorCredits ?? []).map((vendorCredit) => ({
                label: vendorCredit.custom_id || vendorCredit.id,
                value: vendorCredit.id,
                subtext: formatCurrency(vendorCredit.amount_remaining),
                supportText: `Issued ${dayjs(vendorCredit.date_issued).format("MMM D, YYYY")}`,
            })),
        [vendorCredits, formatCurrency],
    )

    const selectedCeiling = getCeiling(form.watch("vendor_credit"))

    // Choosing a credit starts its amount at the most it can give.
    useEffect(() => {
        const subscription = form.watch((values, { name }) => {
            const ceiling = getCeiling(values.vendor_credit)
            if (name === "vendor_credit" && ceiling !== null) {
                form.setValue("amount", ceiling, { shouldValidate: form.formState.isSubmitted })
            }
        })
        return () => subscription.unsubscribe()
    }, [form, getCeiling])

    // A vendor with a single credit has it chosen already, with its full applicable amount.
    useEffect(() => {
        if (vendorCredits?.length === 1) {
            form.setValue("vendor_credit", vendorCredits[0].id)
        }
    }, [form, vendorCredits])

    return (
        <>
            <Dialog.Header.Root>
                <Dialog.Header.Title title="Apply Vendor Credit" />
                <Dialog.Header.CloseButton />
            </Dialog.Header.Root>
            <Dialog.Body>
                <div className={styles.body}>
                    <Select.Root>
                        <Select.Input<BillApplyVendorCreditFormValues>
                            name="vendor_credit"
                            size="md"
                            placeholder="Choose vendor credit..."
                            label="Vendor credit"
                            options={vendorCreditOptions}
                        />
                        <Select.Viewport>
                            <Select.List
                                title="Vendor credit"
                                emptyState={
                                    isVendorCreditsError
                                        ? {
                                              variant: "error",
                                              icon: faIcon({ name: "circle-exclamation", style: "solid" }),
                                              title: "Unable to load vendor credits",
                                              supportText: "Check your connection and try again.",
                                          }
                                        : undefined
                                }
                            >
                                <Select.ListSearch
                                    placeholder="Search by number..."
                                    isLoading={isLoadingVendorCredits}
                                />
                                <Select.ListGroup>
                                    {isLoadingVendorCredits ? (
                                        <>
                                            <Select.ListItem
                                                variant="default"
                                                item={{ label: "", value: "loading-1" }}
                                                isLoading={true}
                                            />
                                            <Select.ListItem
                                                variant="default"
                                                item={{ label: "", value: "loading-2" }}
                                                isLoading={true}
                                            />
                                        </>
                                    ) : (
                                        vendorCreditOptions.map((option) => (
                                            <Select.ListItem
                                                key={option.value}
                                                variant="default"
                                                item={option}
                                                hasSupportText={true}
                                                supportText={option.supportText}
                                                hasSubtext={true}
                                                subtext={option.subtext}
                                            />
                                        ))
                                    )}
                                </Select.ListGroup>
                            </Select.List>
                        </Select.Viewport>
                    </Select.Root>
                    <NumberInput<BillApplyVendorCreditFormValues>
                        name="amount"
                        size="md"
                        label="Amount"
                        min={0.01}
                        step={0.01}
                        isFullWidth={true}
                        inputHelpText={
                            selectedCeiling !== null ? `Up to ${formatCurrency(selectedCeiling)}` : undefined
                        }
                    />
                    {nonFieldError && <AlertBanner type="card" status="error" message={nonFieldError} />}
                </div>
            </Dialog.Body>
            <Dialog.Footer.Root>
                <Button variant="ghost" size="lg" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button variant="solid" size="lg" type="submit" isProcessing={isPending || hasApplied}>
                    Apply Credit
                </Button>
            </Dialog.Footer.Root>
        </>
    )
}

export default function BillApplyVendorCreditDialog(props: BillApplyVendorCreditDialogProps) {
    const { isOpen, onOpenChange, billID, amountDue, vendorID, onApplied } = props

    const [endpoint] = useSlugExtractor([BILLS_ROUTES.APPLY_VENDOR_CREDIT], billID)
    // Applying reloads the page; the button stays busy until then so a second click cannot apply the credit twice.
    const [hasApplied, setHasApplied] = useState(false)

    // Store onOpenChange in a ref so the callback identity stays stable across the legacy card's re-renders.
    // The Popover's useEffect depends on onOpenChange, and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((isNowOpen: boolean) => {
        onOpenChangeRef.current(isNowOpen)
    }, [])

    // The form lives inside the content, which mounts on every opening, so each opening fetches the credits and starts
    // from fresh defaults.
    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <GenericForm<BillApplyVendorCreditFormValues, Bill, MutationErrorBase, BillApplyVendorCreditFormValues>
                    formConfig={{ defaultValues: { vendor_credit: "", amount: "" } }}
                    formValidationSchema={formSchema}
                    mutationConfig={{
                        endpoint,
                        method: "POST",
                        genericErrorMessage: "The vendor credit could not be applied",
                    }}
                    onMutationSuccess={() => {
                        setHasApplied(true)
                        onApplied()
                    }}
                >
                    {({ isPending, nonFieldError, form }) => (
                        <BillApplyVendorCreditFormContent
                            form={form}
                            isPending={isPending}
                            hasApplied={hasApplied}
                            nonFieldError={nonFieldError}
                            amountDue={amountDue}
                            vendorID={vendorID}
                            onCancel={() => stableOnOpenChange(false)}
                        />
                    )}
                </GenericForm>
            </Dialog.Content>
        </Dialog.Root>
    )
}
